import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "@/contexts/AuthContext";
import { ProfileEditor } from "@/components/ProfileEditor";
import { ArrowLeft, RotateCcw } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MyAccountPage = () => {
  const navigate = useNavigate();
  const { user, logout, getUserOrders, isLoggedIn } = useAuth();
  const [orders, setOrders] = useState([]);
  const [loyalty, setLoyalty] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // Get language from localStorage or default to Latvian
  const language = localStorage.getItem('language') || localStorage.getItem('selectedLanguage') || 'lv';

  const translations = {
    title: {
      en: "My Account",
      ru: "Мой аккаунт",
      lv: "Mans konts"
    },
    welcome: {
      en: "Welcome back",
      ru: "Добро пожаловать",
      lv: "Laipni lūdzam atpakaļ"
    },
    orderHistory: {
      en: "Order History",
      ru: "История заказов",
      lv: "Pasūtījumu vēsture"
    },
    loyalty: { en: "Pizza loyalty", ru: "Бонусные пиццы", lv: "Picu lojalitāte" },
    loyaltyNext: { en: "Pizzas until a free one:", ru: "Пицц до бесплатной:", lv: "Picas līdz bezmaksas:" },
    loyaltyRule: { en: "Every 10th pizza is free for registered customers — applied automatically at checkout.", ru: "Каждая 10-я пицца бесплатно для зарегистрированных клиентов — применяется автоматически при оформлении.", lv: "Katra 10. pica reģistrētiem klientiem bez maksas — tiek piemērota automātiski pie norēķiniem." },
    loyaltyEarned: { en: "Free pizzas received", ru: "Получено бесплатных пицц", lv: "Saņemtas bezmaksas picas" },
    freePizzaApplied: { en: "Free pizza", ru: "Бесплатная пицца", lv: "Bezmaksas pica" },
    repeat: { en: "Repeat order", ru: "Повторить заказ", lv: "Atkārtot pasūtījumu" },
    orderNow: { en: "Order now", ru: "Заказать", lv: "Pasūtīt" },
    card: { en: "Card", ru: "Карта", lv: "Karte" },
    cash: { en: "Cash", ru: "Наличные", lv: "Skaidra nauda" },
    status: {
      new: { en: "New", ru: "Новый", lv: "Jauns" },
      preparing: { en: "Preparing", ru: "Готовится", lv: "Gatavo" },
      ready: { en: "Ready", ru: "Готов", lv: "Gatavs" },
      delivering: { en: "Delivering", ru: "Доставляется", lv: "Piegādā" },
      completed: { en: "Completed", ru: "Выполнен", lv: "Izpildīts" },
      cancelled: { en: "Cancelled", ru: "Отменён", lv: "Atcelts" },
    },
    noOrders: {
      en: "No orders yet. Time to order some delicious food!",
      ru: "Пока нет заказов. Время заказать что-то вкусное!",
      lv: "Vēl nav pasūtījumu. Laiks pasūtīt kaut ko garšīgu!"
    },
    orderTotal: {
      en: "Total",
      ru: "Итого",
      lv: "Kopā"
    },
    orderStatus: {
      en: "Status",
      ru: "Статус",
      lv: "Statuss"
    },
    orderDate: {
      en: "Order Date",
      ru: "Дата заказа", 
      lv: "Pasūtījuma datums"
    },
    deliveryType: {
      en: "Type",
      ru: "Тип",
      lv: "Veids"
    },
    delivery: {
      en: "Delivery",
      ru: "Доставка",
      lv: "Piegāde"
    },
    pickup: {
      en: "Pickup",
      ru: "Самовывоз", 
      lv: "Paņemšana"
    },
    logout: {
      en: "Logout",
      ru: "Выйти",
      lv: "Iziet"
    },
    backToHome: {
      en: "Back to Home",
      ru: "На главную",
      lv: "Atpakaļ uz sākumu"
    },
    loading: {
      en: "Loading...",
      ru: "Загрузка...",
      lv: "Ielādē..."
    }
  };

  useEffect(() => {
    if (!isLoggedIn) {
      navigate("/");
      return;
    }
    
    loadOrders();
  }, [isLoggedIn]);

  const loadOrders = async () => {
    try {
      const userOrders = await getUserOrders();
      setOrders(userOrders);
      if (user?.id) {
        const res = await axios.get(`${API}/loyalty/${user.id}`);
        setLoyalty(res.data);
      }
    } catch (error) {
      console.error("Error loading orders:", error);
    } finally {
      setLoading(false);
    }
  };

  const repeatOrder = (order) => {
    localStorage.setItem(
      "rozedinapoli_repeat_order",
      JSON.stringify(order.items.map((i) => ({ menu_item_id: i.menu_item_id, quantity: i.quantity })))
    );
    navigate("/?repeat=1");
  };

  const handleLogout = () => {
    logout();
    navigate("/");
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString('en-GB', {
      timeZone: 'Europe/Riga',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "new": return "bg-blue-100 text-blue-800";
      case "preparing": return "bg-yellow-100 text-yellow-800";
      case "ready": return "bg-green-100 text-green-800";
      case "delivering": return "bg-purple-100 text-purple-800";
      case "completed": return "bg-gray-100 text-gray-800";
      case "cancelled": return "bg-red-100 text-red-800";
      default: return "bg-gray-100 text-gray-800";
    }
  };

  if (!isLoggedIn) {
    return (
      <div className="rz-page min-h-screen flex items-center justify-center px-5">
        <div className="text-center">
          <h1 className="font-display text-3xl text-[#1C1917] mb-6">Access Denied</h1>
          <button onClick={() => navigate("/")} className="rz-btn">Go Home</button>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="rz-page min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b border-[#1C1917] mx-auto mb-4"></div>
          <p className="rz-eyebrow">{translations.loading[language]}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="rz-page min-h-screen" data-testid="my-account-page">
      {/* Header */}
      <div className="border-b border-[#E7E5E4]">
        <div className="max-w-[1100px] mx-auto px-5 sm:px-8 pt-8 pb-10">
          <button
            type="button"
            onClick={() => navigate("/")}
            className="rz-link inline-flex items-center gap-2 min-h-[44px] text-[0.7rem] font-medium tracking-[0.22em] uppercase text-[#78716C]"
            data-testid="account-back-home"
          >
            <ArrowLeft className="w-4 h-4" strokeWidth={1.5} />
            {translations.backToHome[language]}
          </button>
          <div className="mt-6 flex flex-col sm:flex-row sm:items-end justify-between gap-6">
            <div>
              <p className="rz-eyebrow m-0 mb-4">{translations.welcome[language]}, {user.full_name}</p>
              <h1 className="font-display text-[2.4rem] sm:text-[3.2rem] leading-[1] text-[#1C1917] m-0">{translations.title[language]}</h1>
            </div>
            <button onClick={handleLogout} className="rz-btn rz-btn--outline rz-btn--sm self-start sm:self-auto" data-testid="account-logout-button">
              {translations.logout[language]}
            </button>
          </div>
        </div>
      </div>

      {/* Account Content */}
      <div className="max-w-[1100px] mx-auto px-5 sm:px-8 py-12 sm:py-16">
        <ProfileEditor language={language} />

        {/* Loyalty */}
        {loyalty && (
          <section className="grid grid-cols-1 md:grid-cols-12 gap-8 mt-16" data-testid="loyalty-card">
            <div className="md:col-span-4">
              <p className="rz-eyebrow m-0 mb-3">03</p>
              <h2 className="font-display text-[1.9rem] leading-tight text-[#1C1917] m-0">{translations.loyalty[language]}</h2>
            </div>
            <div className="md:col-span-8 md:pl-6 md:border-l border-[#E7E5E4]">
              <div className="flex items-baseline justify-between gap-4">
                <p className="font-display text-[2.6rem] leading-none text-[#1C1917] m-0" data-testid="loyalty-progress">
                  {loyalty.progress}<span className="text-[#A8A29E] text-[1.6rem]">/{loyalty.free_every}</span>
                </p>
                <p className="text-sm text-[#57534E] m-0 text-right" data-testid="loyalty-next">
                  {translations.loyaltyNext[language]} <strong className="text-[#1C1917]">{loyalty.to_next_free}</strong>
                </p>
              </div>
              <div className="mt-4 grid grid-cols-10 gap-1.5" aria-hidden="true">
                {Array.from({ length: loyalty.free_every }).map((_, i) => (
                  <span key={i} className={`h-2 ${i < loyalty.progress ? "bg-[#1C1917]" : "bg-[#E7E5E4]"}`} />
                ))}
              </div>
              <p className="text-sm text-[#78716C] mt-4 m-0">
                {translations.loyaltyRule[language]}
                {loyalty.free_earned > 0 && <> · {translations.loyaltyEarned[language]}: <strong className="text-[#1C1917]">{loyalty.free_earned}</strong></>}
              </p>
            </div>
          </section>
        )}

        {/* Order History */}
        <section className="grid grid-cols-1 md:grid-cols-12 gap-8 mt-16" data-testid="order-history">
          <div className="md:col-span-4">
            <p className="rz-eyebrow m-0 mb-3">04</p>
            <h2 className="font-display text-[1.9rem] leading-tight text-[#1C1917] m-0">{translations.orderHistory[language]}</h2>
          </div>
          <div className="md:col-span-8 md:pl-6 md:border-l border-[#E7E5E4]">
            {orders.length === 0 ? (
              <div data-testid="no-orders">
                <p className="text-[#78716C] m-0 mb-6">{translations.noOrders[language]}</p>
                <button onClick={() => navigate("/")} className="rz-btn rz-btn--outline">{translations.orderNow[language]}</button>
              </div>
            ) : (
              <ul className="list-none p-0 m-0 divide-y divide-[#E7E5E4] border-y border-[#E7E5E4]">
                {orders.map((order) => (
                  <li key={order.id} className="py-6" data-testid={`history-order-${order.id.replace('#', '')}`}>
                    <div className="flex flex-wrap items-baseline justify-between gap-x-6 gap-y-1">
                      <p className="font-display text-[1.25rem] text-[#1C1917] m-0">
                        #{order.id.replace(/^#/, "").slice(0, 8).toUpperCase()}
                        <span className="ml-3 text-[0.62rem] font-body font-medium tracking-[0.18em] uppercase text-[#78716C]">{translations.status[order.order_status]?.[language] || order.order_status}</span>
                      </p>
                      <p className="font-semibold tabular-nums text-[#1C1917] m-0" data-testid="history-order-total">€{order.total_amount.toFixed(2)}</p>
                    </div>
                    <p className="text-[0.72rem] tracking-[0.12em] uppercase text-[#A8A29E] mt-1 m-0">
                      {formatDate(order.created_at)} · {order.delivery_type === "delivery" ? translations.delivery[language] : translations.pickup[language]} · {order.payment_method === "card" ? translations.card[language] : translations.cash[language]}
                    </p>
                    <p className="text-[0.95rem] text-[#57534E] mt-3 m-0 leading-relaxed" data-testid="history-order-items">
                      {order.items.map((item) => `${item.quantity} × ${item.name}`).join(", ")}
                    </p>
                    {order.free_pizzas > 0 && (
                      <p className="text-sm text-[#15803d] mt-2 m-0">{translations.freePizzaApplied[language]} ×{order.free_pizzas} (−€{order.loyalty_discount.toFixed(2)})</p>
                    )}
                    <button onClick={() => repeatOrder(order)} className="rz-btn rz-btn--outline rz-btn--sm mt-4" data-testid="repeat-order-button">
                      <RotateCcw className="w-3.5 h-3.5" strokeWidth={1.5} />
                      {translations.repeat[language]}
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </section>
      </div>
    </div>
  );
};

export default MyAccountPage;
